import { Injectable } from '@angular/core';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';
import { ToastrConfig } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

export interface IToastParams {
    message?: string;
    title?: string;
    config?: Partial<ToastrConfig>;
}

/**
 * Service for showing toast messages.
 */
@Injectable({ providedIn: 'root' })
export class AlertService {
    private successSubject = new Subject<IToastParams>();
    private infoSubject = new Subject<IToastParams>();
    private errorSubject = new Subject<IToastParams>();
    public successStream = this.successSubject.asObservable();
    public infoStream = this.infoSubject.asObservable();
    public errorStream = this.errorSubject.pipe(
        // This 0-second debounce ensures that only the highest-on-the-stack call to
        // `AlertService#error` (the most-recently-invoked one) will result in an alert.
        // E.g. on HTTP error, `ResponseInterceptor` catches, calls `AlertService#error`
        // with a generic message, and re-throws. This gives whoever catches that re-throw
        // the option of overriding the generic alert with a more specific one.
        debounceTime(0)
    );

    constructor() {
        SingletonServiceGuard.register('AlertService');
    }

    public success(message: string, options: IToastParams = {}): void {
        this.successSubject.next({
            message,
            ...options
        });
    }

    public info(message: string, options: IToastParams = {}): void {
        this.infoSubject.next({
            message,
            ...options
        });
    }

    public error(message: string, options: IToastParams = {}): void {
        this.errorSubject.next({
            message,
            ...options
        });
    }
}
